(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wide = window.matchMedia('(min-width: 990px)');

  function initDrops() {
    const items = document.querySelectorAll('[data-dm-drop]');
    if (!('IntersectionObserver' in window) || reduceMotion.matches) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));
  }

  // The theme scrolls an inner wrapper instead of the window, so listen in the capture phase
  // and measure positions from bounding boxes rather than window.scrollY.
  const onAnyScroll = (handler) => document.addEventListener('scroll', handler, { passive: true, capture: true });

  function initHeader() {
    const header = document.querySelector('[data-dm-header]');
    if (!header || !header.classList.contains('dm-header--over')) return;
    const hero = document.querySelector('[data-dm-hero]');
    const update = () => {
      const passed = hero ? hero.getBoundingClientRect().bottom < window.innerHeight * 0.4 : true;
      header.classList.toggle('is-solid', passed);
    };
    update();
    onAnyScroll(update);
  }

  function initHeroVideo() {
    const video = document.querySelector('[data-dm-video]');
    if (!video || reduceMotion.matches) return;
    // Phones get their own portrait cut that follows the subject; without one they keep the still picture
    const portrait = !window.matchMedia('(min-width: 750px)').matches;
    if (portrait && !video.dataset.srcPortrait) return;
    const connection = navigator.connection;
    if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
    const start = () => {
      const small = video.dataset.srcSmall && window.innerWidth < 1280;
      video.muted = true;
      video.src = portrait ? video.dataset.srcPortrait : small ? video.dataset.srcSmall : video.dataset.srcLarge;
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      video.play().catch(() => {});
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function initScrollEffects() {
    if (reduceMotion.matches) return;
    const hero = document.querySelector('[data-dm-hero]');
    const heroMedia = hero && hero.querySelector('[data-dm-hero-media]');
    const heroContent = hero && hero.querySelector('[data-dm-hero-content]');
    const pauses = [...document.querySelectorAll('[data-dm-parallax]')];
    const stages = [...document.querySelectorAll('[data-dm-stage]')].map((stage) => ({
      el: stage,
      items: [...stage.querySelectorAll('[data-dm-step]')],
      spots: [...stage.querySelectorAll('[data-dm-spot]')],
      product: stage.querySelector('[data-dm-stage-product]'),
      current: -1,
    }));

    let ticking = false;

    const render = () => {
      ticking = false;
      const vh = window.innerHeight;

      if (hero && heroMedia) {
        const p = Math.min(Math.max(-hero.getBoundingClientRect().top / vh, 0), 1);
        heroMedia.style.transform = `scale(${1.06 - p * 0.06}) translateY(${p * 6}%)`;
        if (heroContent) {
          heroContent.style.transform = `translateY(${p * -40}px)`;
          heroContent.style.opacity = String(1 - p * 1.1);
        }
      }

      for (const pause of pauses) {
        const media = pause.querySelector('[data-dm-parallax-media]');
        if (!media) continue;
        const rect = pause.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) continue;
        const p = (rect.top + rect.height / 2 - vh / 2) / vh;
        media.style.transform = `translateY(${p * -8}%)`;
      }

      for (const stage of stages) {
        if (!wide.matches || !stage.items.length) {
          stage.items.forEach((el) => el.classList.add('is-active'));
          stage.spots.forEach((el) => el.classList.add('is-active'));
          if (stage.product) stage.product.style.transform = '';
          continue;
        }
        const rect = stage.el.getBoundingClientRect();
        const travel = Math.max(rect.height - vh, 1);
        const p = Math.min(Math.max(-rect.top / travel, 0), 0.999);
        const step = Math.floor(p * stage.items.length);
        if (step !== stage.current) {
          stage.current = step;
          stage.items.forEach((el, i) => el.classList.toggle('is-active', i === step));
          stage.spots.forEach((el, i) => el.classList.toggle('is-active', i === step));
        }
        if (stage.product) {
          const rotate = -6 + p * 12;
          const scale = 1 + Math.sin(p * Math.PI * stage.items.length) * 0.04 + 0.04;
          stage.product.style.transform = `rotate(${rotate.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        }
      }
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(render);
    };

    render();
    onAnyScroll(request);
    window.addEventListener('resize', request);
  }

  function initStageFallback() {
    if (!reduceMotion.matches) return;
    document.querySelectorAll('[data-dm-step], [data-dm-spot]').forEach((el) => el.classList.add('is-active'));
  }

  function initOffer() {
    const form = document.getElementById('dm-offer-form');
    if (!form) return;
    const data = form.querySelector('[data-dm-variants]');
    const idInput = form.querySelector('[data-dm-variant-id]');
    const priceEl = form.querySelector('[data-dm-price]');
    const addButton = form.querySelector('[data-dm-add]');
    if (!data || !idInput) return;

    let variants = [];
    try {
      variants = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }

    const bar = document.querySelector('[data-dm-buybar]');
    const barVariant = bar?.querySelector('[data-dm-buybar-variant]');
    const barPrice = bar?.querySelector('[data-dm-buybar-price]');
    const barButton = bar?.querySelector('[data-dm-buybar-add]');

    const groups = [...form.querySelectorAll('[data-dm-option]')];
    // Photos per colour: a shot whose value is selected wins, otherwise the default shot shows
    const shots = [...(form.closest('.dm-offer')?.querySelectorAll('[data-dm-shot]') || [])];
    const showShot = (values) => {
      const match = shots.find((shot) => shot.dataset.dmShot && values.includes(shot.dataset.dmShot));
      shots.forEach((shot) => shot.classList.toggle('is-active', match ? shot === match : !shot.dataset.dmShot));
    };
    const money = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);

    const selected = () => groups.map((group) => group.querySelector('input:checked')?.value);

    const update = () => {
      const values = selected();
      showShot(values);
      const variant = variants.find((v) => v.options.every((option, i) => values[i] === undefined || option === values[i]));
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (barButton) barButton.disabled = true;
        return;
      }
      idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? addButton.dataset.labelAdd : addButton.dataset.labelSoldout;
      }
      if (barVariant) barVariant.textContent = variant.options.join(' · ');
      if (barPrice) barPrice.textContent = money(variant.price);
      if (barButton) barButton.disabled = !variant.available;
      groups.forEach((group, index) => {
        for (const cardPrice of group.querySelectorAll('[data-dm-card-price]')) {
          const candidate = variants.find((v) =>
            v.options.every((option, i) => (i === index ? option === cardPrice.dataset.dmCardPrice : values[i] === undefined || option === values[i]))
          );
          if (candidate) cardPrice.textContent = money(candidate.price);
        }
      });
    };

    form.addEventListener('change', (event) => {
      if (event.target.matches('[data-dm-option] input')) update();
    });
    update();
    initBuyBar(bar, form);
  }

  // Buttons with data-dm-pick choose that package in the buy section before jumping to it
  function initPick() {
    const form = document.getElementById('dm-offer-form');
    if (!form) return;
    for (const link of document.querySelectorAll('[data-dm-pick]')) {
      link.addEventListener('click', () => {
        const input = [...form.querySelectorAll('[data-dm-option] input')].find((el) => el.value === link.dataset.dmPick);
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  // Looping section videos load only when they come near the screen and pause while off screen.
  // The visible button pauses them for good; with reduced motion they wait for that button.
  function initFilms() {
    for (const section of document.querySelectorAll('[data-dm-film]')) {
      const video = section.querySelector('[data-dm-film-video]');
      const toggle = section.querySelector('[data-dm-film-toggle]');
      if (!video || !video.dataset.src) continue;
      let stoppedByUser = reduceMotion.matches;
      let onScreen = false;

      const load = () => {
        if (!video.getAttribute('src')) video.src = video.dataset.src;
      };
      const setLabel = () => {
        if (!toggle) return;
        const playing = !video.paused;
        toggle.classList.toggle('is-playing', playing);
        toggle.setAttribute('aria-label', playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay);
      };
      const play = () => {
        load();
        video.muted = true;
        video.play().catch(() => {});
      };

      video.addEventListener('play', setLabel);
      video.addEventListener('pause', setLabel);
      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          if (video.paused) {
            stoppedByUser = false;
            play();
          } else {
            stoppedByUser = true;
            video.pause();
          }
        });
      }
      setLabel();

      if (!('IntersectionObserver' in window)) {
        if (!stoppedByUser) play();
        continue;
      }
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            onScreen = entry.isIntersecting;
            if (onScreen && !stoppedByUser) play();
            else if (!onScreen && !video.paused) video.pause();
          }
        },
        { rootMargin: '200px 0px', threshold: 0 }
      ).observe(section);
    }
  }

  // Show the phone buy bar while neither the form's own buttons nor the footer are on screen,
  // so it never doubles the main button or covers the legal links at the end of the page.
  function initBuyBar(bar, form) {
    if (!bar || !('IntersectionObserver' in window)) return;
    const watched = [form.querySelector('[data-dm-add]'), form.querySelector('.dm-offer__express'), document.querySelector('.dm-footer')].filter(Boolean);
    if (!watched.length) return;
    bar.hidden = false;
    const onScreen = new Set();
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }
      bar.classList.toggle('is-visible', onScreen.size === 0);
    });
    watched.forEach((el) => observer.observe(el));
  }

  const start = () => {
    initDrops();
    initHeader();
    initHeroVideo();
    initScrollEffects();
    initStageFallback();
    initOffer();
    initPick();
    initFilms();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
